import React, { FC, useMemo, useState } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  FilterConfig,
  FilterType,
  Flex,
  Table,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import {
  faArrowDownToLine,
  faFileInvoiceDollar,
  faFilter,
} from '@fortawesome/pro-regular-svg-icons';
import SectionCard from './SectionCard';
import { financeController, BillingReport } from '@/apis/core';
import { useDispatch } from 'react-redux';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import { generateAndDownloadFile } from '@/utils/download';
import { toastFlashMessage } from '@/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const getThreeYearsAgoMonth = () => {
  const currentDate = new Date();
  const threeYearsAgoDate = new Date(
    currentDate.getFullYear() - 3,
    currentDate.getMonth()
  );
  const year = threeYearsAgoDate.getFullYear();
  const month = String(threeYearsAgoDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const FILTER_CONFIGS: Record<string, FilterConfig> = {
  startMonth: {
    type: FilterType.DatePicker,
    label: 'Start Month',
    scope: 'month',
    minDate: getThreeYearsAgoMonth(),
  },
  endMonth: {
    type: FilterType.DatePicker,
    label: 'End Month',
    scope: 'month',
    minDate: getThreeYearsAgoMonth(),
  },
};

const headRows = [{ id: 'month', label: 'Monthly Reports', sortable: false }];

const MONTHS_PER_YEAR = 12;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const TabFinance: FC = () => {
  const dispatch = useDispatch();
  const setLoadingMessage = (message: string) => {
    dispatch({
      type: SET_LOADING_MESSAGE,
      payload: message,
    });
  };

  const clearLoadingMessage = () => {
    dispatch({
      type: CLEAR_LOADING_MESSAGE,
    });
  };

  const [filtersShown, setFiltersShown] = useState(false);

  const filter = useFilters({
    configs: FILTER_CONFIGS,
    inMemory: true,
  });

  const table = useTable<BillingReport>(
    async (tableFilter) => {
      const query = {
        ...tableFilter,
        ...filter.appliedFilters,
        ...filter.additionalFilters,
      };
      const response = await financeController.getBillingReports(query);

      return {
        data: response?.records || [],
        page: tableFilter?.page || 1,
        rowsPerPage: MONTHS_PER_YEAR,
        total: response?.totalRecords || 0,
      };
    },
    {
      deps: [filter.appliedFilters, filter.additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const formatMonthYear = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    if (!year || !month) return monthStr;

    const monthIndex = parseInt(month, 10) - 1;
    const monthName = MONTH_NAMES[monthIndex] || month;
    return `${monthName} ${year}`;
  };

  const tableData = useMemo(() => {
    return table.tableData.map((report: BillingReport) => ({
      month: (
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>{formatMonthYear(report.month)}</Box>
          <Box
            sx={{
              width: 12,
              height: 12,
              fontSize: 16,
              cursor: 'pointer',
              mr: 10,
            }}
            onClick={() => handleDownload(report.month)}
            data-testid="downloadReportButton"
          >
            <FontAwesomeIcon icon={faArrowDownToLine} />
          </Box>
        </Flex>
      ),
    }));
  }, [table.tableData]);

  const getReportFileName = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    if (!year || !month) return `${monthStr}_billing_report.csv`;

    const monthIndex = parseInt(month, 10) - 1;
    const monthName = (MONTH_NAMES[monthIndex] || month).toLowerCase();
    return `${monthName}_${year}_billing_report.csv`;
  };

  const handleDownload = async (month: string) => {
    setLoadingMessage('We are generating the document');
    const response = await financeController.downloadBillingReport(month);
    if (response) {
      const fileName = getReportFileName(month);
      const file = new File([response], fileName);
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download billing report', 'error');
    }
    clearLoadingMessage();
  };

  return (
    <SectionCard
      icon={faFileInvoiceDollar}
      title="Billing Reports"
      description="View up to 3 years of report data."
      dataTestId="billingReportsSection"
    >
      <Flex sx={{ alignItems: 'center', mb: '12px', mt: '-4px' }}>
        <Button
          startAdornment={faFilter}
          onClick={() => setFiltersShown(!filtersShown)}
          data-testid="tableAddFilterButton"
        >
          <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
        </Button>
      </Flex>
      {filtersShown && (
        <Box sx={{ mb: 12 }}>
          <TableFilter
            configs={filter.configs}
            candidateValues={filter.candidateFilters}
            appliedValues={filter.appliedFilters}
            onCandidateValuesChange={filter.handleEdit}
            onAppliedValuesChange={filter.handleApply}
            data-testid="billingReportTableFilter"
          />
        </Box>
      )}
      <Table
        testId="billingReportTable"
        headRows={headRows}
        emptyState="no billing report"
        disableHover
        loading={table.loading}
        data={tableData}
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        filter={table.filter}
        pagination={table.pagination}
        emptyBlockHeight="150px"
      />
    </SectionCard>
  );
};

export default TabFinance;
